/**
 * A helper service containing utility functions for displaying various types of notifications
 */
export class UserMessagesService {

  public errorDialogVisible: boolean;

  constructor(
    private $mdDialog: ng.material.IDialogService,
    private $mdToast: ng.material.IToastService,
    private $document: ng.IDocumentService,
    private $translate
  ) {
  }

  /**
   * Shows an error dialog with the input error message, 
   * if an error dialog is not already being shown.
   *
   * @param error The error message to show
   */
  public showErrorDialog = (error: string) => {
    if (!this.errorDialogVisible) {
      this.$mdDialog.show({
        controller: 'ErrorDialogController',
        controllerAs: 'vm',
        templateUrl: 'app/angularJS/components/error_dialog/error_dialog.html',
        parent: angular.element(this.$document[0].body),
        locals: {
          response: error
        }
      });

      this.errorDialogVisible = true;
    }
  }

  /**
   * Shows a list dialog with the title input, allows selection of one list item.
   *
   * @param title The title of the dialog
   * @param list The list of items to select from
   * @param okText The text for the OK button
   * @param cancelText The text for the Cancel button
   * @returns the promise of a response from $mdDialog of the same input type as the list items
   */
  public showListDialog = <T>(title: string, list: Array<T>, okText: string, cancelText: string): ng.IPromise<T> => {
    return this.$mdDialog.show({
      controller: 'ListDialogController',
      controllerAs: 'vm',
      templateUrl: 'app/angularJS/components/list_dialog/list_dialog.html',
      parent: angular.element(this.$document[0].body),
      locals: {
        title: title,
        list: list,
        okText: okText,
        cancelText: cancelText
      }
    });
  }

  /**
   * Shows a manual entry dialog with options to enter a custom oid, typ, ptyp and cpicc value
   *
   * @returns the promise of a response from $mdDialog of the same input type as the manual entry items
   */
  public showManualEntryDialog = <T>(title: string, okText: string, cancelText: string): ng.IPromise<T> => {
    return this.$mdDialog.show({
      controller: 'ManualEntryDialogController',
      controllerAs: 'vm',
      templateUrl: 'app/angularJS/components/manual_entry_dialog/manual_entry_dialog.html',
      parent: angular.element(this.$document[0].body),
      locals: {
        title: title,
        okText: okText,
        cancelText: cancelText
      }
    });
  }

  /**
   * Shows a toast message containing the message and variables entered
   *
   * @param message The message entered
   * @param variables A set of variables to be passed into the message translation
   * @returns the promise of a response from $mdDialog
   */
  public showToast = (message: string, variables?: Object) => {
    return this.$mdToast.show({
      template: '<md-toast><span>' + this.$translate.instant(message, variables) + '</span></md-toast>',
      position: 'top right',
      hideDelay: 5000,
      parent: this.$document[0].querySelector('#toast-container')
    });
  }

  /**
   * Shows a confirmation dialog acknowledging the accepting of a certain action
   *
   * @param title The title of the dialog
   * @param content The content of the dialog
   * @param variables A set of variables to pass to the dialog template
   * @param okText The text for the OK button
   * @param cancelText The text for the Cancel button
   * @returns the promise of a response from $mdDialog
   */
  public buildConfirmDialog = (
    title: string,
    content: string,
    variables: any,
    okText?: string,
    cancelText?: string
  ) => {
    const confirm = {
      controller: 'ConfirmDialogController',
      controllerAs: 'vm',
      templateUrl: 'app/angularJS/components/confirm_dialog/confirm_dialog.html',
      parent: angular.element(this.$document[0].body),
      locals: {
        title: title,
        content: content,
        variables: variables,
        okText: okText,
        cancelText: cancelText
      }
    };
    return this.$mdDialog.show(confirm);
  }

  /**
   * Shows an error toast containing a message entered
   *
   * @param message The message of the error toast
   * @param variables A set of variables to pass to the dialog template
   * @param callbackFn A function to callback with on accepting
   * @param btnText The text for the accept button
   * @returns the promise of a response from $mdDialog
   */
  public showErrorToast = (message: string, variables: Object, callbackFn: Function, btnText: string) => {
    const toastr = this.$mdToast;
    this.$mdToast.show({
      template: '<md-toast class="error">' +
        '<span>' + this.$translate.instant(message, variables) + '</span>' +
        '<md-button ng-click="vm.callbackFn()"><span translate>{{vm.btnText}}</span></md-button>' +
        '</md-toast>',
      position: 'top right',
      hideDelay: 0,
      parent: this.$document[0].querySelector('#toast-container'),
      controllerAs: 'vm',
      // tslint:disable-next-line:only-arrow-functions
      controller: function () {
        const vm = this;
        vm.callbackFn = () => {
          toastr.hide();

          if (callbackFn) {
            callbackFn();
          }
        },
          vm.btnText = btnText;
      }
    });
  }

  /**
   * Displays a simple alert dialog using supplied text values for the title and main content area
   *
   * @param title Text to show in the header of the dialog
   * @param message Text to show in the main content area of the dialog
   * @returns the promise of a response from $mdDialog
   */
  public showAlert = (title: string, message: string) => {
    return this.$mdDialog.show(
      this.$mdDialog.alert()
        .parent(this.$document[0].body)
        .clickOutsideToClose(true)
        .title(this.$translate.instant(title))
        .textContent(this.$translate.instant(message))
        .ok(this.$translate.instant('ACTION_DETAIL.OK'))
        .ariaLabel('Alert Dialog')
    );
  }
}
